import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
    Search,
    Plus,
    Copy,
    Pencil,
    Trash2,
    CopyPlus,
    Eye,
    Store,
    QrCode,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import QRCodeDialog from "@/components/QRCodeDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [qr, setQr] = useState(null); // {slug, name}

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/businesses");
            setBusinesses(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return businesses;
        return businesses.filter((b) => {
            return (
                b.slug?.toLowerCase().includes(q) ||
                b.name?.en?.toLowerCase().includes(q) ||
                b.name?.ar?.toLowerCase().includes(q)
            );
        });
    }, [businesses, query]);

    const handleToggle = async (b) => {
        try {
            const { data } = await api.post(`/businesses/${b.id}/toggle`);
            setBusinesses((list) => list.map((x) => (x.id === b.id ? { ...x, is_active: data.is_active } : x)));
            toast.success(data.is_active ? "Page activated" : "Page deactivated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleDuplicate = async (b) => {
        try {
            const { data } = await api.post(`/businesses/${b.id}/duplicate`);
            setBusinesses((list) => [data, ...list]);
            toast.success("Business duplicated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleDelete = async (b) => {
        try {
            await api.delete(`/businesses/${b.id}`);
            setBusinesses((list) => list.filter((x) => x.id !== b.id));
            toast.success("Business deleted");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const publicUrl = (slug) => `${window.location.origin}/b/${slug}`;

    const copyUrl = async (slug) => {
        try {
            await navigator.clipboard.writeText(publicUrl(slug));
            toast.success("Public URL copied");
        } catch {
            toast.error("Could not copy URL");
        }
    };

    return (
        <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-2">Businesses</div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">
                        All NFC business pages
                    </h1>
                    <p className="mt-2 text-zinc-400 text-sm" data-testid="business-count">
                        {filtered.length} shown · {businesses.length} total
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                        <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search business…"
                            className="ps-9 w-full sm:w-64 h-10 bg-zinc-900 border-white/10 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-brand"
                            data-testid="search-businesses-input"
                        />
                    </div>
                    <Link to="/admin/new">
                        <Button className="h-10 bg-brand hover:bg-brand-hover text-white font-medium w-full sm:w-auto" data-testid="new-business-button">
                            <Plus className="h-4 w-4 me-1.5" />
                            New business
                        </Button>
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-48 rounded-xl bg-white/5 animate-pulse" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-white/10 rounded-xl p-16 text-center bg-zinc-900/40">
                    <div className="mx-auto h-12 w-12 rounded-xl bg-brand/15 text-brand flex items-center justify-center mb-4">
                        <Store className="h-6 w-6" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-zinc-100">No businesses yet</h3>
                    <p className="text-sm text-zinc-400 mt-1">Create your first NFC business page.</p>
                    <Link to="/admin/new" className="inline-block mt-6">
                        <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="empty-new-business-button">
                            <Plus className="h-4 w-4 me-1.5" />
                            New business
                        </Button>
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="business-list">
                    {filtered.map((b) => (
                        <div
                            key={b.id}
                            data-testid={`business-card-${b.slug}`}
                            className="group bg-zinc-900/70 border border-white/10 rounded-xl p-5 transition-transform hover:-translate-y-0.5 hover:border-white/20"
                        >
                            <div className="flex items-start gap-3">
                                {b.background_url ? (
                                    <img
                                        src={fileUrl(b.background_url)}
                                        alt=""
                                        className="h-12 w-12 rounded-lg object-cover border border-white/10"
                                    />
                                ) : (
                                    <div
                                        className="h-12 w-12 rounded-lg border border-white/10 flex items-center justify-center text-zinc-300"
                                        style={{ backgroundColor: b.background_color || "#27272a" }}
                                    >
                                        <Store className="h-5 w-5" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold truncate text-zinc-100">
                                        {b.name?.en || b.slug}
                                    </div>
                                    <div className="text-xs text-zinc-500 truncate font-mono">/b/{b.slug}</div>
                                </div>
                                <Switch
                                    checked={!!b.is_active}
                                    onCheckedChange={() => handleToggle(b)}
                                    data-testid={`toggle-active-${b.slug}`}
                                    aria-label="Toggle active"
                                />
                            </div>

                            <div className="mt-4 flex items-center gap-2 text-xs text-zinc-400">
                                <span
                                    className={`inline-block h-1.5 w-1.5 rounded-full ${b.is_active ? "bg-emerald-500" : "bg-zinc-600"}`}
                                />
                                {b.is_active ? "Active" : "Inactive"}
                                <span className="mx-1">·</span>
                                {(b.buttons?.length || 0)} button{(b.buttons?.length || 0) === 1 ? "" : "s"}
                            </div>

                            <div className="mt-5 flex flex-wrap items-center gap-1.5">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => copyUrl(b.slug)}
                                    data-testid={`copy-url-${b.slug}`}
                                    className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5"
                                >
                                    <Copy className="h-3.5 w-3.5 me-1.5" />
                                    URL
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setQr({ slug: b.slug, name: b.name?.en || b.slug })}
                                    data-testid={`qr-${b.slug}`}
                                    className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5"
                                >
                                    <QrCode className="h-3.5 w-3.5 me-1.5" />
                                    QR
                                </Button>
                                <a href={`/b/${b.slug}`} target="_blank" rel="noreferrer">
                                    <Button variant="outline" size="sm" data-testid={`preview-${b.slug}`}
                                        className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5">
                                        <Eye className="h-3.5 w-3.5 me-1.5" />
                                        Preview
                                    </Button>
                                </a>
                                <Link to={`/admin/edit/${b.id}`}>
                                    <Button variant="outline" size="sm" data-testid={`edit-${b.slug}`}
                                        className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5">
                                        <Pencil className="h-3.5 w-3.5 me-1.5" />
                                        Edit
                                    </Button>
                                </Link>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDuplicate(b)}
                                    data-testid={`duplicate-${b.slug}`}
                                    className="text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
                                >
                                    <CopyPlus className="h-3.5 w-3.5 me-1.5" />
                                    Duplicate
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ms-auto"
                                            data-testid={`delete-${b.slug}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-zinc-900 border-white/10 text-zinc-100">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete this business?</AlertDialogTitle>
                                            <AlertDialogDescription className="text-zinc-400">
                                                This action cannot be undone. The public page at{" "}
                                                <span className="font-mono">/b/{b.slug}</span> will stop working.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel className="bg-transparent border-white/10 text-zinc-200 hover:bg-white/5">Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => handleDelete(b)}
                                                className="bg-red-600 hover:bg-red-700 text-white"
                                                data-testid={`confirm-delete-${b.slug}`}
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <QRCodeDialog
                open={!!qr}
                onOpenChange={(v) => !v && setQr(null)}
                url={qr ? publicUrl(qr.slug) : ""}
                businessName={qr?.name}
            />
        </div>
    );
}
